import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  fetchNotifications, markRead, startRealtimeNotifications, requestPermission, seedSeenIds,
} from '../services/NotificationService';
import { fireNotificationToast } from './NotificationToast';
import { useCustomerAuth } from '../context/CustomerAuthContext';

const TYPE_COLOR = {
  chat:    '#0ABAB5',
  order:   '#f59e0b',
  driver:  '#3b82f6',
  payment: '#10b981',
  promo:   '#a855f7',
  info:    '#64748b',
};

function TypeIcon({ type, color, size = 18 }) {
  const s = { width: size, height: size, display: 'block' };
  if (type === 'chat') return (
    <svg style={{ width: size, height: size, flexShrink:0 }} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
  );
  if (type === 'order') return (
    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V10"/>
      <path d="M23 3H1v7h22V3z"/>
      <line x1="12" y1="3" x2="12" y2="21"/>
    </svg>
  );
  if (type === 'driver') return (
    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13" rx="2"/>
      <path d="M16 8h4l3 5v3h-7V8z"/>
      <circle cx="5.5" cy="18.5" r="2.5"/>
      <circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
  );
  if (type === 'payment') return (
    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2"/>
      <line x1="1" y1="10" x2="23" y2="10"/>
      <line x1="6" y1="16" x2="10" y2="16"/>
    </svg>
  );
  if (type === 'promo') return (
    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 12 20 22 4 22 4 12"/>
      <rect x="2" y="7" width="20" height="5" rx="1"/>
      <line x1="12" y1="22" x2="12" y2="7"/>
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
    </svg>
  );
  return (
    <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

export default function NotificationBell({ isDark }) {
  const { customer } = useCustomerAuth();
  const [open, setOpen]       = useState(false);
  const [notifs, setNotifs]   = useState([]);
  const [unread, setUnread]   = useState(0);
  const [perm, setPerm]       = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const panelRef = useRef(null);
  const btnRef   = useRef(null);

  const textC  = isDark ? '#e2e8f0' : '#0f172a';
  const muted  = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)';
  const bg     = isDark ? '#0d1b2a' : '#ffffff';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.09)';
  const itemBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  const unreadBg = isDark ? 'rgba(10,186,181,0.1)' : 'rgba(10,186,181,0.07)';

  /* Load notifications */
  const load = useCallback(async () => {
    if (!customer) return;
    const list = await fetchNotifications();
    // Mark all existing IDs as seen so they don't re-fire
    seedSeenIds(list.map(n => n.id));
    setNotifs(list);
    setUnread(list.filter(n => !n.is_read).length);
  }, [customer]);

  /* Update browser tab title with unread count */
  useEffect(() => {
    const base = 'وترلي';
    document.title = unread > 0 ? `(${unread}) ${base}` : base;
  }, [unread]);

  useEffect(() => {
    if (!customer) return;
    let stop = () => {};
    // Wait for load (seedSeenIds) to finish BEFORE starting realtime
    load().then(() => {
      stop = startRealtimeNotifications(customer.id, (fresh, incoming) => {
        fresh.forEach(n => fireNotificationToast(n));
        setNotifs(prev => {
          const merged = [...fresh, ...prev].slice(0, 50);
          setUnread(merged.filter(n => !n.is_read).length);
          return merged;
        });
      });
    });
    return () => stop();
  }, [customer, load]);

  /* Close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (open && panelRef.current && !panelRef.current.contains(e.target)
          && btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  /* Open panel → mark all read */
  const handleOpen = async () => {
    if (!open) {
      setOpen(true);
      if (unread > 0) {
        await markRead();
        setUnread(0);
        setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
      }
    } else {
      setOpen(false);
    }
  };

  /* Request push permission */
  const handleRequestPerm = async () => {
    const r = await requestPermission();
    setPerm(r);
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60)  return 'الآن';
    if (diff < 3600) return `${Math.floor(diff/60)} د`;
    if (diff < 86400) return `${Math.floor(diff/3600)} س`;
    return d.toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' });
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        ref={btnRef}
        onClick={handleOpen}
        title="الإشعارات"
        style={{
          position: 'relative',
          width: 32, height: 32,
          borderRadius: '50%',
          border: isDark ? '1px solid rgba(10,186,181,0.3)' : '1px solid rgba(10,186,181,0.35)',
          background: isDark ? 'rgba(10,186,181,0.12)' : 'rgba(10,186,181,0.1)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.15s, background 0.2s',
          outline: 'none',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke={isDark ? '#0ABAB5' : '#0369a1'}
          strokeWidth="1.8" width="16" height="16">
          <path strokeLinecap="round" d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path strokeLinecap="round" d="M13.73 21a2 2 0 0 1-3.46 0"/>
          <path d="M12 3v1" strokeLinecap="round"/>
        </svg>
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -3, right: -3,
            background: '#ef4444', color: '#fff',
            borderRadius: '50%', minWidth: 16, height: 16,
            fontSize: '0.55rem', fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 2px',
            border: `2px solid ${isDark ? '#0d1b2a' : '#fff'}`,
            animation: 'nb-pulse 1.5s ease-in-out infinite',
          }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: btnRef.current ? btnRef.current.getBoundingClientRect().bottom + 10 : 50,
            right: 12,
            width: 300,
            maxHeight: 420,
            borderRadius: '1rem',
            background: bg,
            border: `1px solid ${border}`,
            boxShadow: isDark
              ? '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(10,186,181,0.08)'
              : '0 12px 40px rgba(0,0,0,0.12)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 9999,
            fontFamily: "'Cairo', sans-serif",
            direction: 'rtl',
            animation: 'nb-in 0.3s cubic-bezier(0.16,1,0.3,1) both',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.75rem 1rem',
            borderBottom: `1px solid ${border}`,
            background: isDark ? 'rgba(10,186,181,0.06)' : 'rgba(10,186,181,0.04)',
          }}>
            <span style={{ fontWeight: 900, fontSize: '0.85rem', color: textC }}>الإشعارات</span>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {perm !== 'granted' && perm !== 'unsupported' && (
                <button
                  onClick={handleRequestPerm}
                  title="تفعيل إشعارات الجهاز"
                  style={{
                    fontSize: '0.65rem', fontWeight: 700, color: '#0ABAB5',
                    background: 'rgba(10,186,181,0.1)', border: '1px solid rgba(10,186,181,0.3)',
                    borderRadius: '0.4rem', padding: '0.2rem 0.5rem', cursor: 'pointer',
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  🔔 تفعيل إشعارات الجهاز
                </button>
              )}
              {perm === 'granted' && (
                <span style={{ fontSize: '0.6rem', color: '#10b981' }}>✓ مفعّل</span>
              )}
            </div>
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {notifs.length === 0 ? (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '2rem 1rem', gap: '0.5rem',
              }}>
                <span style={{ fontSize: '2rem' }}>🔕</span>
                <span style={{ fontSize: '0.75rem', color: muted, fontWeight: 600 }}>لا توجد إشعارات</span>
              </div>
            ) : notifs.map(n => (
              <div key={n.id} style={{
                display: 'flex', gap: '0.6rem', padding: '0.7rem 1rem',
                borderBottom: `1px solid ${border}`,
                background: n.is_read ? 'transparent' : unreadBg,
                transition: 'background 0.15s',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '0.65rem', flexShrink: 0,
                  background: `${TYPE_COLOR[n.type] || '#64748b'}18`,
                  border: `1.5px solid ${TYPE_COLOR[n.type] || '#64748b'}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                }}>
                  <TypeIcon type={n.type} color={TYPE_COLOR[n.type] || '#64748b'} size={24} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.3rem' }}>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: n.is_read ? 600 : 800,
                      color: textC, lineHeight: 1.4,
                    }}>{n.title}</span>
                    <span style={{ fontSize: '0.58rem', color: muted, flexShrink: 0, marginTop: 2 }}>
                      {fmtTime(n.created_at)}
                    </span>
                  </div>
                  {n.body && (
                    <p style={{
                      fontSize: '0.65rem', color: muted, marginTop: '0.15rem',
                      lineHeight: 1.5, overflow: 'hidden',
                      display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>{n.body}</p>
                  )}
                </div>
                {!n.is_read && (
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#0ABAB5', flexShrink: 0, marginTop: 4 }}/>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes nb-pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }
        @keyframes nb-in {
          from { opacity:0; transform:translateY(-8px) scale(0.96); }
          to   { opacity:1; transform:translateY(0)    scale(1);    }
        }
      `}</style>
    </div>
  );
}
